import type { IssueId } from '@atlassian/jira-shared-types';
import type {
	ChartData,
	DateRange,
	IssueMetrics,
	IssueTableData,
	IssueTableRow,
} from '../../common/types';
import type { BarChartData } from '../historical-cycle-time/types';
import { getSelectedBarChartBarDataAndItsIndex } from '../historical-cycle-time/utils';

const findIssueRowByIssueId = (
	issueTableRows: IssueTableRow[],
	id: IssueId,
): IssueTableRow | Error => {
	const foundIssueRow = issueTableRows.find((x) => x.issue.id === id);
	return foundIssueRow ?? Error(`Couldn't find issue with issue id ${id} in issueTable data`);
};

export const transformData = (
	issueMetrics: IssueMetrics[],
	barChartData: BarChartData,
	selectedDateRange: DateRange,
	issueTableData: IssueTableData,
): ChartData => {
	const { barData: selectedPeriodBarData } = getSelectedBarChartBarDataAndItsIndex(
		barChartData.barData,
		selectedDateRange,
	);
	return {
		hasPermissionForAllContributingIssues: barChartData.hasPermissionForAllContributingIssues,
		hasEnoughDataForMeaningfulStatistics: barChartData.hasEnoughDataForMeaningfulStatistics,
		periodCycleTimeInSeconds: selectedPeriodBarData.cycleTimeStatistics,
		wholePeriodCycleTimeInSeconds: barChartData.wholePeriodStatistics,
		dataPoints: issueMetrics.map((x) => {
			const { lastSuccessfulProductionDeployment, totalCycleTimeSeconds, id: issueId } = x;

			const issueTableRowOrError = findIssueRowByIssueId(issueTableData.rows, issueId);

			if (issueTableRowOrError instanceof Error) {
				return {
					deployedDate: lastSuccessfulProductionDeployment,
					value: totalCycleTimeSeconds,
					customInfo: {
						loading: false,
						error: issueTableRowOrError,
					},
				};
			}
			const {
				issue: { key: issueKey },
				reviewTime,
				numPullRequests,
				numCommits,
			} = issueTableRowOrError;

			return {
				deployedDate: lastSuccessfulProductionDeployment,
				value: totalCycleTimeSeconds,
				customInfo: {
					loading: false,
					issueKey,
					tooltipFormatCycleTime: totalCycleTimeSeconds,
					reviewTime,
					numPullRequests,
					numCommits,
				},
			};
		}),
	};
};
